import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  getGiftEventLogListForAgencyAdmin,
  GiftEventLogBugo,
  GiftEventLogItem,
  GiftEventType,
} from '@shared/api/giftEventLog/giftEventLog.controller';
import {
  giftItemListForBugoAgencyAdminDataAtom,
  giftItemListForBugoAgencyAdminStatusAtom,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForBugoAgencyAdminHook } from '@shared/api/giftItem/giftItem.hook';
import {
  giftOrderListForBugoAgencyAdminDataAtom,
  giftOrderListForBugoAgencyAdminStatusAtom,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderListForBugoAgencyAdminHook } from '@shared/api/giftOrder/giftOrder.hook';
import { Loading } from '@shared/components/Loading';
import StatPageSideBar from '@shared/components/StatPageSideBar';

const formatDate = (date: Date | string) => {
  const d = new Date(date);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getBugoId = (bugo: string | GiftEventLogBugo): string =>
  typeof bugo === 'object' && bugo !== null ? bugo._id : bugo;

const getFeventId = (bugo: string | GiftEventLogBugo): string | undefined =>
  typeof bugo === 'object' && bugo !== null ? bugo.fevent : undefined;

const eventLabel: Record<string, string> = {
  courtesy_view: '답례글 페이지',
  store_view: '답례품 목록',
  item_view: '상품 상세',
};

const eventColor: Record<string, string> = {
  courtesy_view: 'bg-blue-100 text-blue-800',
  store_view: 'bg-purple-100 text-purple-800',
  item_view: 'bg-orange-100 text-orange-800',
};

const getDateRange = (range: 'today' | '7d' | '30d' | 'all') => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  let start: Date | undefined;

  switch (range) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case '7d':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      break;
    case '30d':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      break;
    default:
      start = undefined;
  }

  return {
    startDate: start?.toISOString(),
    endDate: end.toISOString(),
  };
};

const FunnelBar: FC<{
  label: string;
  count: number;
  logCount: number;
  maxCount: number;
  color: string;
  revenue?: number;
}> = ({ label, count, logCount, maxCount, color, revenue }) => {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <div className="text-right">
          <span className="font-bold text-gray-900">
            {count}명
            <span className="ml-1 text-xs font-normal text-gray-500">({logCount}회)</span>
          </span>
          {revenue != null && revenue > 0 && (
            <div className="text-xs font-bold text-green-600">
              {revenue.toLocaleString()}원
            </div>
          )}
        </div>
      </div>
      <div className="h-8 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`flex h-full items-center rounded-full px-3 text-xs font-bold text-white transition-all duration-500 ${color}`}
          style={{ width: `${Math.max(pct, 2)}%` }}
        >
          {pct >= 10 && `${pct.toFixed(1)}%`}
        </div>
      </div>
    </div>
  );
};

interface FunnelStats {
  courtesy_view: number;
  store_view: number;
  item_view: number;
  purchased: number;
  purchasedOrders: number;
  purchasedRevenue: number;
  totalBugos: number;
  totalFevents: number;
  courtesy_view_logs: number;
  store_view_logs: number;
  item_view_logs: number;
}

const AgencyAdminGiftEventLogContent: FC = function AgencyAdminGiftEventLogContent() {
  const [range, setRange] = useState<'today' | '7d' | '30d' | 'all'>('7d');
  const [logs, setLogs] = useState<GiftEventLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'funnel' | 'logs'>('funnel');
  const [showItemDetail, setShowItemDetail] = useState(false);
  const [showPurchaseDetail, setShowPurchaseDetail] = useState(false);

  useGiftOrderListForBugoAgencyAdminHook('default');
  const orderStatus = useRecoilValue(
    giftOrderListForBugoAgencyAdminStatusAtom('default'),
  );
  const orderList = useRecoilValue(giftOrderListForBugoAgencyAdminDataAtom('default'));

  useGiftItemListForBugoAgencyAdminHook();
  const giftItemListStatus = useRecoilValue(giftItemListForBugoAgencyAdminStatusAtom);
  const giftItemList = useRecoilValue(giftItemListForBugoAgencyAdminDataAtom);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(range);
      const data = await getGiftEventLogListForAgencyAdmin(startDate, endDate);
      setLogs(data);
    } catch (error) {
      console.error('이벤트 로그 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const stats: FunnelStats = useMemo(() => {
    const bugoEvents = new Map<string, Set<GiftEventType>>();
    const feventIds = new Set<string>();

    logs.forEach((log) => {
      const bugoId = getBugoId(log.bugo);
      const feventId = getFeventId(log.bugo);
      if (feventId) feventIds.add(feventId);

      const existing = bugoEvents.get(bugoId);
      if (existing) {
        existing.add(log.event);
      } else {
        bugoEvents.set(bugoId, new Set([log.event]));
      }
    });

    let courtesyView = 0;
    let storeView = 0;
    let itemView = 0;

    bugoEvents.forEach((events) => {
      if (events.has('courtesy_view')) courtesyView++;
      if (events.has('store_view')) storeView++;
      if (events.has('item_view')) itemView++;
    });

    let courtesyViewLogs = 0;
    let storeViewLogs = 0;
    let itemViewLogs = 0;
    logs.forEach((log) => {
      if (log.event === 'courtesy_view') courtesyViewLogs++;
      else if (log.event === 'store_view') storeViewLogs++;
      else if (log.event === 'item_view') itemViewLogs++;
    });

    const bugoIdsWithEvents = new Set(bugoEvents.keys());
    let purchased = 0;
    let purchasedOrders = 0;
    let purchasedRevenue = 0;
    if (orderList) {
      const bugoOrderInfo = new Map<string, { count: number; revenue: number }>();
      orderList.forEach((order) => {
        const bugo = order.bugo;
        const bugoId =
          typeof bugo === 'object' && bugo !== null ? bugo._id : String(bugo);
        const orderRevenue =
          order.orderDetails?.reduce(
            (sum, d) => sum + (d.giftItemEmbed?.priceRetail ?? 0) * (d.quantity ?? 1),
            0,
          ) ?? 0;
        const existing = bugoOrderInfo.get(bugoId);
        if (existing) {
          existing.count += 1;
          existing.revenue += orderRevenue;
        } else {
          bugoOrderInfo.set(bugoId, { count: 1, revenue: orderRevenue });
        }
      });
      bugoIdsWithEvents.forEach((bugoId) => {
        const info = bugoOrderInfo.get(bugoId);
        if (info) {
          purchased++;
          purchasedOrders += info.count;
          purchasedRevenue += info.revenue;
        }
      });
    }

    return {
      courtesy_view: courtesyView,
      store_view: storeView,
      item_view: itemView,
      purchased,
      purchasedOrders,
      purchasedRevenue,
      totalBugos: bugoEvents.size,
      totalFevents: feventIds.size,
      courtesy_view_logs: courtesyViewLogs,
      store_view_logs: storeViewLogs,
      item_view_logs: itemViewLogs,
    };
  }, [logs, orderList]);

  const funnelData = useMemo(() => {
    const counts = [
      stats.courtesy_view,
      stats.store_view,
      stats.item_view,
      stats.purchased,
    ];
    const maxCount = Math.max(...counts, 1);

    return [
      {
        label: '① 답례글 페이지 진입',
        count: stats.courtesy_view,
        logCount: stats.courtesy_view_logs,
        maxCount,
        color: 'bg-blue-500',
        revenue: undefined as number | undefined,
        detailType: undefined as string | undefined,
      },
      {
        label: '② 답례품 목록 진입',
        count: stats.store_view,
        logCount: stats.store_view_logs,
        maxCount,
        color: 'bg-purple-500',
        revenue: undefined as number | undefined,
        detailType: undefined as string | undefined,
      },
      {
        label: '③ 상품 상세 조회',
        count: stats.item_view,
        logCount: stats.item_view_logs,
        maxCount,
        color: 'bg-orange-500',
        revenue: undefined as number | undefined,
        detailType: 'itemView' as string | undefined,
      },
      {
        label: '④ 구매 완료',
        count: stats.purchased,
        logCount: stats.purchasedOrders,
        maxCount,
        color: 'bg-green-500',
        revenue: stats.purchasedRevenue,
        detailType: 'purchase' as string | undefined,
      },
    ];
  }, [stats]);

  const conversionRates = useMemo(() => {
    const rate = (a: number, b: number) => (b > 0 ? ((a / b) * 100).toFixed(1) : '-');
    return [
      {
        label: '답례글 → 답례품 목록',
        value: rate(stats.store_view, stats.courtesy_view),
      },
      {
        label: '답례품 목록 → 상세',
        value: rate(stats.item_view, stats.store_view),
      },
      {
        label: '상세 → 구매',
        value: rate(stats.purchased, stats.item_view),
      },
      {
        label: '전체 전환율 (답례글→구매)',
        value: rate(stats.purchased, stats.courtesy_view),
      },
    ];
  }, [stats]);

  // 상품별 상세 조회 집계
  const itemViewDetails = useMemo(() => {
    const itemBugos = new Map<string, Set<string>>();
    const itemLogCounts = new Map<string, number>();
    logs.forEach((log) => {
      if (log.event === 'item_view' && log.giftItem) {
        const bugoId = getBugoId(log.bugo);
        const bugos = itemBugos.get(log.giftItem);
        if (bugos) {
          bugos.add(bugoId);
        } else {
          itemBugos.set(log.giftItem, new Set([bugoId]));
        }
        itemLogCounts.set(log.giftItem, (itemLogCounts.get(log.giftItem) || 0) + 1);
      }
    });

    const giftItemMap = new Map<string, { name: string; price: number }>();
    if (giftItemList) {
      giftItemList.forEach((item) => {
        const displayName =
          item.name ||
          (typeof item.giftItemInfo === 'object' && item.giftItemInfo?.name) ||
          item._id;
        giftItemMap.set(item._id, {
          name: String(displayName),
          price: item.priceRetail,
        });
      });
    }

    return Array.from(itemBugos.entries())
      .map(([itemId, bugos]) => {
        const info = giftItemMap.get(itemId);
        return {
          itemId,
          name: info?.name || itemId.slice(-6),
          price: info?.price ?? 0,
          logCount: itemLogCounts.get(itemId) || 0,
          bugoCount: bugos.size,
        };
      })
      .sort((a, b) => b.bugoCount - a.bugoCount);
  }, [logs, giftItemList]);

  // 상품별 구매 집계
  const purchaseDetails = useMemo(() => {
    if (!orderList) return [];

    const itemBugos = new Map<string, Set<string>>();
    const itemOrderCounts = new Map<string, number>();
    const itemQuantities = new Map<string, number>();
    const itemRevenues = new Map<string, number>();
    const itemNames = new Map<string, { name: string; price: number }>();

    orderList.forEach((order) => {
      const bugoId =
        typeof order.bugo === 'object' && order.bugo !== null
          ? order.bugo._id
          : String(order.bugo);

      order.orderDetails?.forEach((detail) => {
        const giftItem = detail.giftItem;
        const itemId =
          typeof giftItem === 'object' && giftItem !== null
            ? giftItem._id
            : String(giftItem);
        const qty = detail.quantity ?? 1;
        const unitPrice = detail.giftItemEmbed?.priceRetail ?? 0;

        const bugos = itemBugos.get(itemId);
        if (bugos) {
          bugos.add(bugoId);
        } else {
          itemBugos.set(itemId, new Set([bugoId]));
        }
        itemOrderCounts.set(itemId, (itemOrderCounts.get(itemId) || 0) + 1);
        itemQuantities.set(itemId, (itemQuantities.get(itemId) || 0) + qty);
        itemRevenues.set(itemId, (itemRevenues.get(itemId) || 0) + unitPrice * qty);

        if (!itemNames.has(itemId)) {
          itemNames.set(itemId, {
            name: detail.giftItemEmbed?.name || itemId.slice(-6),
            price: unitPrice,
          });
        }
      });
    });

    return Array.from(itemBugos.entries())
      .map(([itemId, bugos]) => {
        const info = itemNames.get(itemId);
        return {
          itemId,
          name: info?.name || itemId.slice(-6),
          price: info?.price ?? 0,
          orderCount: itemOrderCounts.get(itemId) || 0,
          quantity: itemQuantities.get(itemId) || 0,
          revenue: itemRevenues.get(itemId) || 0,
          bugoCount: bugos.size,
        };
      })
      .sort((a, b) => b.bugoCount - a.bugoCount);
  }, [orderList]);

  const eventCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const bugos: Record<string, Set<string>> = {};
    logs.forEach((log) => {
      counts[log.event] = (counts[log.event] || 0) + 1;
      const set = bugos[log.event] ?? new Set<string>();
      set.add(getBugoId(log.bugo));
      bugos[log.event] = set;
    });
    return { counts, bugos };
  }, [logs]);

  if (
    loading ||
    orderStatus === 'loading' ||
    orderStatus === 'idle' ||
    giftItemListStatus === 'loading' ||
    giftItemListStatus === 'idle'
  ) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">이벤트 로그</h1>
        <p className="mt-1 text-sm text-gray-500">
          답례품 구매 전 사용자 행동 퍼널 분석 (부고 단위 집계)
        </p>
      </div>

      <div className="mb-6 flex gap-2">
        {(
          [
            { key: 'today', label: '오늘' },
            { key: '7d', label: '7일' },
            { key: '30d', label: '30일' },
            { key: 'all', label: '전체' },
          ] as const
        ).map((item) => (
          <button
            key={item.key}
            onClick={() => setRange(item.key)}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              range === item.key
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex border-b border-gray-200">
        <button
          onClick={() => setTab('funnel')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'funnel'
              ? 'border-b-2 border-indigo-600 text-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          퍼널 분석
        </button>
        <button
          onClick={() => setTab('logs')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'logs'
              ? 'border-b-2 border-indigo-600 text-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          로그 목록 ({logs.length})
        </button>
      </div>

      {tab === 'funnel' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <div className="rounded-lg bg-white p-4 shadow">
              <p className="text-xs text-gray-500">고유 부고 수</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {stats.totalBugos}명
                {stats.totalFevents > 0 && (
                  <span className="ml-1 text-sm font-normal text-gray-500">
                    ({stats.totalFevents}상가)
                  </span>
                )}
              </p>
            </div>
            {funnelData.map((item) => (
              <div key={item.label} className="rounded-lg bg-white p-4 shadow">
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {item.count}명
                  <span className="ml-1 text-sm font-normal text-gray-500">
                    ({item.logCount}회)
                  </span>
                </p>
                {item.revenue != null && item.revenue > 0 && (
                  <p className="mt-0.5 text-sm font-bold text-green-600">
                    {item.revenue.toLocaleString()}원
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-bold text-gray-800">퍼널 (부고 단위)</h2>
            <div className="space-y-4">
              {funnelData.map((item) => (
                <div key={item.label}>
                  <FunnelBar {...item} />
                  {item.detailType === 'itemView' && (
                    <div className="mt-2">
                      <button
                        onClick={() => setShowItemDetail((prev) => !prev)}
                        className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        <span>{showItemDetail ? '▼' : '▶'}</span>
                        <span>상품별 상세보기 ({itemViewDetails.length}개 상품)</span>
                      </button>
                      {showItemDetail && (
                        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                          {itemViewDetails.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              상품 상세 조회 기록이 없습니다.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              <div className="mb-2 flex items-center justify-between text-xs font-medium text-gray-500">
                                <span>상품명 / 가격</span>
                                <span>조회 수</span>
                              </div>
                              {itemViewDetails.map((detail) => {
                                const maxDetailCount = itemViewDetails[0]?.bugoCount || 1;
                                const pct = (detail.bugoCount / maxDetailCount) * 100;
                                return (
                                  <div key={detail.itemId}>
                                    <div className="flex items-center justify-between text-sm">
                                      <div className="flex max-w-[60%] items-baseline gap-2">
                                        <span
                                          className="truncate text-gray-700"
                                          title={detail.name}
                                        >
                                          {detail.name}
                                        </span>
                                        {detail.price > 0 && (
                                          <span className="shrink-0 text-xs text-gray-400">
                                            {detail.price.toLocaleString()}원
                                          </span>
                                        )}
                                      </div>
                                      <span className="font-bold text-gray-900">
                                        {detail.bugoCount}명
                                        <span className="ml-1 text-xs font-normal text-gray-500">
                                          ({detail.logCount}회)
                                        </span>
                                      </span>
                                    </div>
                                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                      <div
                                        className="h-full rounded-full bg-orange-400 transition-all duration-300"
                                        style={{
                                          width: `${Math.max(pct, 2)}%`,
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {item.detailType === 'purchase' && (
                    <div className="mt-2">
                      <button
                        onClick={() => setShowPurchaseDetail((prev) => !prev)}
                        className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        <span>{showPurchaseDetail ? '▼' : '▶'}</span>
                        <span>상품별 상세보기 ({purchaseDetails.length}개 상품)</span>
                      </button>
                      {showPurchaseDetail && (
                        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                          {purchaseDetails.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              구매 완료 기록이 없습니다.
                            </p>
                          ) : (
                            <div className="space-y-3">
                              <div className="mb-2 flex items-center justify-between text-xs font-medium text-gray-500">
                                <span>상품명 / 가격</span>
                                <span>수량 / 매출</span>
                              </div>
                              {purchaseDetails.map((detail) => {
                                const maxDetailCount = purchaseDetails[0]?.bugoCount || 1;
                                const pct = (detail.bugoCount / maxDetailCount) * 100;
                                return (
                                  <div key={detail.itemId}>
                                    <div className="flex items-center justify-between text-sm">
                                      <div className="flex max-w-[50%] items-baseline gap-2">
                                        <span
                                          className="truncate text-gray-700"
                                          title={detail.name}
                                        >
                                          {detail.name}
                                        </span>
                                        {detail.price > 0 && (
                                          <span className="shrink-0 text-xs text-gray-400">
                                            {detail.price.toLocaleString()}원
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <span className="font-bold text-gray-900">
                                          {detail.quantity}개
                                        </span>
                                        <span className="ml-1 text-xs font-normal text-gray-500">
                                          ({detail.bugoCount}명 / {detail.orderCount}건)
                                        </span>
                                        <div className="text-xs text-green-600">
                                          {detail.revenue.toLocaleString()}원
                                        </div>
                                        <div className="text-xs text-gray-400">
                                          객단가{' '}
                                          {detail.bugoCount > 0
                                            ? Math.round(
                                                detail.revenue / detail.bugoCount,
                                              ).toLocaleString()
                                            : 0}
                                          원/명 ·{' '}
                                          {detail.orderCount > 0
                                            ? Math.round(
                                                detail.revenue / detail.orderCount,
                                              ).toLocaleString()
                                            : 0}
                                          원/건
                                        </div>
                                      </div>
                                    </div>
                                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                      <div
                                        className="h-full rounded-full bg-green-400 transition-all duration-300"
                                        style={{
                                          width: `${Math.max(pct, 2)}%`,
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                              {(() => {
                                const totalQty = purchaseDetails.reduce(
                                  (s, d) => s + d.quantity,
                                  0,
                                );
                                const totalRevenue = purchaseDetails.reduce(
                                  (s, d) => s + d.revenue,
                                  0,
                                );
                                const totalOrders = purchaseDetails.reduce(
                                  (s, d) => s + d.orderCount,
                                  0,
                                );
                                return (
                                  <div className="mt-3 flex items-center justify-between border-t border-gray-300 pt-3">
                                    <span className="text-sm font-bold text-gray-700">
                                      합계
                                    </span>
                                    <div className="text-right">
                                      <span className="font-bold text-gray-900">
                                        {totalQty.toLocaleString()}개
                                      </span>
                                      <div className="text-sm font-bold text-green-600">
                                        {totalRevenue.toLocaleString()}원
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        객단가{' '}
                                        {stats.purchased > 0
                                          ? Math.round(
                                              totalRevenue / stats.purchased,
                                            ).toLocaleString()
                                          : 0}
                                        원/명 ·{' '}
                                        {totalOrders > 0
                                          ? Math.round(
                                              totalRevenue / totalOrders,
                                            ).toLocaleString()
                                          : 0}
                                        원/건
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-bold text-gray-800">단계별 전환율</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {conversionRates.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between rounded-lg border border-gray-100 p-4"
                >
                  <span className="text-sm text-gray-600">{item.label}</span>
                  <span className="text-lg font-bold text-indigo-600">
                    {item.value === '-' ? '-' : `${item.value}%`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-bold text-gray-800">
              이벤트별 총 로그 수 (중복 포함)
            </h2>
            <div className="space-y-2">
              {Object.entries(eventCounts.counts).map(([event, count]) => (
                <div key={event} className="flex items-center justify-between">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      eventColor[event] || 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {eventLabel[event] || event}
                  </span>
                  <span className="font-bold text-gray-900">
                    {eventCounts.bugos[event]?.size ?? 0}명
                    <span className="ml-1 text-xs font-normal text-gray-500">
                      ({count}회)
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'logs' && (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  시각
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  이벤트
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  부고 ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상품 ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  세션
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    로그가 없습니다.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                      {formatDate(log.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          eventColor[log.event] || 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {eventLabel[log.event] || log.event}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-500">
                      {getBugoId(log.bugo)?.slice(-6) || '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-500">
                      {log.giftItem?.slice(-6) || '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-400">
                      {log.sessionId?.slice(0, 8) || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const AgencyAdminGiftEventLogPage: FC = () => (
  <div className="flex">
    <div className="flex flex-col">
      <StatPageSideBar />
    </div>
    <div className="mx-auto max-w-screen-2xl flex-1">
      <div className="py-8 px-10">
        <AgencyAdminGiftEventLogContent />
      </div>
    </div>
  </div>
);
